import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {CustomDataTableSortLogic, PrimeNgConfig, PrimeNgSupportService, TableApp} from "@app/shared";
import {DomHandler} from "primeng/dom";
import {ObjectUtils} from "primeng/utils";
import {Table, TableService} from "primeng/table";
import {AbstractDataTableComponent} from "@app/shared/table/abstract-data-table/abstract-data-table.component";
import {SortEvent} from "primeng/api";

export function tableFactory(tableComponent: DataTableComponent): Table {
  return tableComponent.table;
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  providers: [
    DomHandler,// from new imports
    ObjectUtils,// from new imports
    TableService,// from old imports
    {
      provide: Table,// providing table class
      useFactory: tableFactory,
      deps: [DataTableComponent],// new function depends on your wrapper
    },
  ],
})
export class DataTableComponent extends AbstractDataTableComponent implements OnInit, AfterViewInit {

  TableApp: typeof TableApp = TableApp;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;

  @Input() contextMenuSelectionMode: string;
  @Input() expandedRowKeys: { [s: string]: boolean; } = {};
  @Input() rowExpandMode: string;

  _expandAllRows: boolean;
  @Input()
  set expandAllRows(val: boolean) {
    this._expandAllRows = val;
  }

  get expandAllRows(): any {
    return this._expandAllRows;
  }

  @Input() stateStorage: string = "session";
  @Input() stateKey: string = null;

  @ViewChild('dt', {static: true}) public table: Table;

  @Output() dataTable = new EventEmitter<Table>();

  constructor(public primeNgService: PrimeNgSupportService) {
    super();
  }

  ngOnInit(): void {
    this.setDefaultProperties();
  }

  ngAfterViewInit() {
    this.dataTable.emit(this.table);
  }

  onClickExpandOrCollapseAllRows(): void {
    if (!this.allRowsExpanded) {
      this.value.forEach(value => {
        this.expandedRowKeys[value[this.dataKey]] = true;
      });
    } else {
      this.expandedRowKeys = {};
    }
    this.allRowsExpanded = !this.allRowsExpanded;
  }

  setDefaultProperties(): void {

    if (!this.rows) {
      this.rows = PrimeNgConfig.TableRows;
    }

    if (!this.rowsPerPageOptions) {
      this.rowsPerPageOptions = PrimeNgConfig.TableRowsPerPage;
    }

    if (!this.sortMode) {
      this.sortMode = "single";
    }
    if (!this.rows) {
      this.rows = PrimeNgConfig.TableRows;
    }
    if (!this.sortOrder) {
      this.sortOrder = PrimeNgConfig.TableDesSortOrder;
    }
    if (this.selectionMode === this.contextMenuSelection) {
      this.contextMenuSelectionMode = "joint";
    } else {
      this.contextMenuSelectionMode = "separate";
    }
    if (!this.rowExpandMode) {
      this.rowExpandMode = "multiple";
    }
  }

  sortFunction(event: SortEvent, customSortField: string, customerSortLogic: CustomDataTableSortLogic) {
    //event.data = Data to sort
    //event.mode = 'single' or 'multiple' sort mode
    //event.field = Sort field in single sort
    //event.order = Sort order in single sort
    //event.multiSortMeta = SortMeta array in multiple sort

    event.data.sort((data1, data2) => {

      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result;

      if (value1 == null && value2 != null) {
        result = -1;
      } else if (value1 != null && value2 == null) {
        result = 1;
      } else if (value1 == null && value2 == null) {
        result = 0;
      } else if (event.field == customSortField) {
        // 0) AC.L1-3.1.2   AC.L1-3.1.20
        // 1) AC.L1.3.1.2   AC.L1.3.1.20
        // 2) AC L1 3 1 2   AC L1 3 1 20,       AC L1 3 1   AC L1 3 1 20,     AC L1 3 1 2   AC L1 3 1

        let valueArray1: Array<any>, valueArray2: Array<any>;
        if (customerSortLogic === CustomDataTableSortLogic.CMMC_REFERENCE_NUMBER) {
          valueArray1 = this.splitReferenceToArray(value1);
          valueArray2 = this.splitReferenceToArray(value2);
        } else if (customerSortLogic === CustomDataTableSortLogic.CMMC_CONTROL_NUMBER) {
          valueArray1 = this.splitControlToArray(value1);
          valueArray2 = this.splitControlToArray(value2);
        }

        let l = 0, m = Math.min(valueArray1.length, valueArray2.length);
        while (l < m && valueArray1[l] === valueArray2[l]) {
          l++;
        }
        result = this.compareStringOrNumber(valueArray1[l], valueArray2[l]);
        // console.log(`comparing customSortField: ${value1}  ${value2}  result = ${result}`);
        // console.log(`last field: ${valueArray1[l]}  ${valueArray2[l]}`);
      } else if (typeof value1 === 'string' && typeof value2 === 'string') {
        result = value1.localeCompare(value2);
      } else {
        result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
      }

      return (event.order * result);
    });
  }

  // previous split logic for sorting entire reference number AC.L1-3.1.2 by
  splitReferenceToArray(inputValue: string): Array<any> {
    let fieldValue = inputValue.replace('-', '.');
    let fieldArray: Array<any> = fieldValue.split('.');
    fieldArray.forEach((value, index) => {
      if (!isNaN(value)) {
        fieldArray[index] = Number(value);
      }
    });
    return fieldArray;
  }

  // split logic for sorting control number (3.1.2) from the reference AC.L1-3.1.2
  splitControlToArray(inputValue: string): Array<any> {
    let tempArr: Array<string> = inputValue.split('-');
    tempArr = tempArr[1].split('.');
    let outArr: Array<number> = new Array<number>();
    tempArr.forEach((value, index) => {
      outArr[index] = Number(value);
    });
    return outArr;
  }

  compareStringOrNumber(value1, value2): number {
    let result: number;
    if (value1 == null && value2 != null) {
      result = -1;
    } else if (value1 != null && value2 == null) {
      result = 1;
    } else if (value1 == null && value2 == null) {
      result = 0;
    } else if (typeof value1 === 'string' && typeof value2 === 'string') {
      result = value1.localeCompare(value2);
    } else {
      result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
    }
    return result;
  }
}
